import { useMemo, useState, useEffect } from 'react';
import Collapse from '@mui/material/Collapse';
import PropTypes from 'prop-types';
import { IconChevronDown, IconChevronUp, IconEye } from '@tabler/icons-react';
import { useForm, yupResolver } from '@mantine/form';
import debounce from 'lodash.debounce';

import Button from '../buttons/Button';
import FormInput from './FormInput';
import SelectInput from '../inputs/SelectInput';
import Label from '../inputs/Label';
import HelperText from '../inputs/HelperText';
import { useTransactional } from '../../store/transactional/hooks';
import {
  DEBOUNCE_DELAY_TIME_MS,
  donationTypes,
  MAP_TAG_NAME_TO_ACCOUNT_INFO,
  MAP_TRANSACTIONAL_EMAILS_TO_TEMPLATE_NAME,
  PREVIEW_HTML_KEY,
  TRANSACTIONAL_EMAILS,
} from '../../core/constants';
import ImageInput from './ImageInput';
import {
  getCustomSubstitutionTagDefaultValues,
  replaceCustomTagsWithSendgridTags,
  substituteTagWithValue,
} from '../../core/utils';
import TextEditorInput from './TextEditorInput';
import { useAccount } from '../../store/account/hooks';
import { emailValidation } from './validation';
import appSettings from '../../app-settings';
import EmailStatus from './EmailStatus';

const MONTHS = [
  { label: 'January', value: '01' },
  { label: 'February', value: '02' },
  { label: 'March', value: '03' },
  { label: 'April', value: '04' },
  { label: 'May', value: '05' },
  { label: 'June', value: '06' },
  { label: 'July', value: '07' },
  { label: 'August', value: '08' },
  { label: 'September', value: '09' },
  { label: 'October', value: '10' },
  { label: 'November', value: '11' },
  { label: 'December', value: '12' },
];

const getDaysInMonth = (month) => {
  const daysMap = {
    '01': 31,
    '02': 28,
    '03': 31,
    '04': 30,
    '05': 31,
    '06': 30,
    '07': 31,
    '08': 31,
    '09': 30,
    10: 31,
    11: 30,
    12: 31,
  };
  return daysMap[month] || 31;
};

const getDayOptions = (month) => {
  const max = getDaysInMonth(month);
  return Array.from({ length: max }, (_, i) => {
    const day = String(i + 1).padStart(2, '0');
    return { label: day, value: day };
  });
};

const ExpandableForm = ({ isExpanded, onClick, title, keyAccessor, setImageFiles }) => {
  const [editorKey, setEditorKey] = useState(Date.now().toString());
  const { emails, updateTransactionalEmail } = useTransactional();
  const { account } = useAccount();

  const initialValues = emails[keyAccessor];

  const form = useForm({
    initialValues,
    validate: yupResolver(emailValidation),
    validateInputOnChange: true,
  });

  useEffect(() => {
    let logo = initialValues.image;

    if (initialValues.image.includes('placeholder')) {
      if (account?.emailPreferences?.logo && !account.emailPreferences.logo.includes('placeholder')) {
        logo = account.emailPreferences.logo;
      } else if (account?.logoUrl) {
        logo = account.logoUrl;
      }
    }

    if (logo !== initialValues.image) {
      form.setFieldValue('image', logo);
      updateTransactionalEmail({ email: keyAccessor, image: logo });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    account?.emailPreferences?.logo,
    keyAccessor,
    initialValues.image,
    account?.logoUrl,
    updateTransactionalEmail,
    form.setFieldValue,
  ]);

  const debouncedUpdateTransactionalEmail = useMemo(
    () => debounce(updateTransactionalEmail, DEBOUNCE_DELAY_TIME_MS),
    [updateTransactionalEmail],
  );

  const handlePreview = async (e) => {
    e.stopPropagation();

    const templateName = MAP_TRANSACTIONAL_EMAILS_TO_TEMPLATE_NAME[keyAccessor];

    let html = (await import(`./templates/${templateName}.html?raw`))?.default;

    if (!html) return;

    html = html.replace(
      /<div id="main"(.*?)<\/div>/,
      `
      <div style="line-height: 140%; text-align: left; word-wrap: break-word; font-family: Lato, sans-serif; font-size: 16px; color:{{TextColor}}">
        ${form.values.text}
      </div>
      <br />
    `,
    );

    const customTagDefaultValues = getCustomSubstitutionTagDefaultValues(html);
    html = replaceCustomTagsWithSendgridTags(html);

    const testDonationAmount = 250;
    let donationType = donationTypes.recurring;

    if (keyAccessor === TRANSACTIONAL_EMAILS.oneTimeFailed) {
      donationType = donationTypes.oneTime;
    }

    const donationUrl = account?.donationPageSubdomain
      ? `https://${account?.donationPageSubdomain}.${appSettings.donationPageDomain}/?amount=${250}&type=${donationType}`
      : '';

    const substitutionTags = {
      ...customTagDefaultValues,
      Heading: form.values.heading,
      PrimaryColor: account?.emailPreferences?.primaryColor,
      BodyBackground: account?.emailPreferences?.bodyBackground,
      HeaderColor: account?.emailPreferences?.headerColor,
      Logo: form.values.image || account?.emailPreferences?.logo,
      TextColor: account?.emailPreferences?.textColor,
      FooterBackground: account?.emailPreferences?.footerBackground,
      FooterTextColor: account?.emailPreferences?.footerTextColor,
      FirstName: 'friend',
      DonationAmount: testDonationAmount.toFixed(2),
      DonationUrl: donationUrl,
      TributeType: 'honor',
      HonoreeName: '{{HonoreeName}}',
      RecipientName: '{{RecipientName}}',
      Message: '{{Message}}',
      BillingFrequency: donationType === donationTypes.recurring ? 'monthly' : '',
    };

    Object.keys(customTagDefaultValues).forEach((tagName) => {
      const value = substitutionTags[tagName] || account[MAP_TAG_NAME_TO_ACCOUNT_INFO[tagName]] || '';
      html = substituteTagWithValue(html, tagName, value);
    });

    localStorage.setItem(PREVIEW_HTML_KEY, html);
    window.open('/template-preview', '_blank');
  };

  const handleChange = (field, value) => {
    if (field === 'image') {
      const url = URL.createObjectURL(value);
      setImageFiles((prevState) => ({ ...prevState, [keyAccessor]: value }));
      form.setFieldValue(field, url);
      debouncedUpdateTransactionalEmail({ email: keyAccessor, [field]: url });
      return;
    }
    form.setFieldValue(field, value);
    debouncedUpdateTransactionalEmail({ email: keyAccessor, [field]: value });
  };

  const handleCancel = (field) => {
    const initialValue = emails[keyAccessor][field] ?? '';
    form.setFieldValue(field, initialValue);
    debouncedUpdateTransactionalEmail({ email: keyAccessor, [field]: initialValue });
    setTimeout(() => form.clearFieldError(field), 0);
    if (field === 'text') {
      setEditorKey(Date.now().toString());
    }
  };

  const headerColorClassName = form.values.send ? 'text-gray-950' : 'text-gray-400';

  return (
    <div className="w-full px-4 py-2">
      <div className="flex cursor-pointer items-center justify-between" onClick={onClick}>
        <div className="flex flex-1 space-x-2">
          {isExpanded ? (
            <IconChevronUp size={20} className={`mt-0.5 ${headerColorClassName}`} />
          ) : (
            <IconChevronDown size={20} className={`mt-0.5 ${headerColorClassName}`} />
          )}
          <div className="space-y-2">
            <p className={`font-bold ${headerColorClassName}`}>{title}</p>
            {isExpanded && <EmailStatus enabled={form.values.send} />}
          </div>
        </div>
        {isExpanded && <Button title="Preview" color="primary" onClick={handlePreview} LeftIcon={IconEye} />}
      </div>
      <Collapse in={isExpanded}>
        <div className="space-y-6 pb-4 pl-8 pr-2 pt-8">
          <FormInput
            id="subject"
            label="Subject"
            value={form.values.subject}
            onChange={(e) => handleChange('subject', e.target.value)}
            error={form.errors.subject}
            onReset={() => handleCancel('subject')}
            required
          />
          <ImageInput
            value={form.values.image}
            onChange={(file) => handleChange('image', file)}
            onReset={() => handleCancel('image')}
            required
          />
          <FormInput
            id="heading"
            label="Heading"
            value={form.values.heading}
            onChange={(e) => handleChange('heading', e.target.value)}
            error={form.errors.heading}
            onReset={() => handleCancel('heading')}
            required
          />
          <TextEditorInput
            key={editorKey}
            id={keyAccessor}
            label="Text"
            value={form.values.text}
            onChange={(value) => handleChange('text', value)}
            error={form.errors.text}
            onReset={() => handleCancel('text')}
            required
          />
          {keyAccessor === TRANSACTIONAL_EMAILS.annualDonationsReceipt && (
            // Mirrors FormInput's layout so this row lines up with the ones above it:
            // a fixed label column, the inputs to its right, and an empty quarter where
            // the other rows put their Edit button (the send date is always editable).
            <div className="flex">
              <div className="w-3/4">
                <div className="flex items-start">
                  <Label id="sendDate-month" label="Send date" labelClassName="mr-[5%] w-[125px] shrink-0 pt-2" />
                  <div className="flex-1">
                    <div className="flex gap-3">
                      <SelectInput
                        id="sendDate-month"
                        aria-label="Send date month"
                        value={(form.values.sendDate || '01-05').split('-')[0]}
                        options={MONTHS}
                        onChange={(e) => {
                          const newMonth = e.target.value;
                          const currentDay = (form.values.sendDate || '01-05').split('-')[1];
                          const maxDay = getDaysInMonth(newMonth);
                          const clampedDay =
                            parseInt(currentDay, 10) > maxDay ? String(maxDay).padStart(2, '0') : currentDay;
                          handleChange('sendDate', `${newMonth}-${clampedDay}`);
                        }}
                        containerClassName="w-44"
                      />
                      <SelectInput
                        id="sendDate-day"
                        aria-label="Send date day"
                        value={(form.values.sendDate || '01-05').split('-')[1]}
                        options={getDayOptions((form.values.sendDate || '01-05').split('-')[0])}
                        onChange={(e) => {
                          const currentMonth = (form.values.sendDate || '01-05').split('-')[0];
                          handleChange('sendDate', `${currentMonth}-${e.target.value}`);
                        }}
                        containerClassName="w-24"
                      />
                    </div>
                    <HelperText message="Sent on this date every year." containerClassName="mt-1.5" />
                  </div>
                </div>
              </div>
              <div className="w-1/4" />
            </div>
          )}
        </div>
      </Collapse>
    </div>
  );
};

ExpandableForm.propTypes = {
  isExpanded: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  keyAccessor: PropTypes.string.isRequired,
  setImageFiles: PropTypes.func.isRequired,
};

export default ExpandableForm;
